import { getApiEndpoint } from './config.js';
import { checkLogin, checkToken } from './account.js';

/**
 * Get tags (and groups).
 *
 * @param {object} token - The token to use, for getting conversations.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversations.
 */
export function getTags(token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/tag-groups', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Create a tag group.
 *
 * @param {object} tagGroup - The tag group to create.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function createTagGroup(tagGroup, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/tag-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify(tagGroup),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Create a tag.
 *
 * @param {int} tagGroupId - The id of the auto reply.
 * @param {object} tag - The tag to save.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function createTag(tagGroupId, tag, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/tag-groups/' + tagGroupId + '/tags', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify(tag),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Delete a tag group.
 *
 * @param {int} tagGroupId - The id of the tag group to delete.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function deleteTagGroup(tagGroupId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/tag-groups/' + tagGroupId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Delete a tag.
 *
 * @param {int} tagGroupId - The id of the tag group.
 * @param {int} tagId - The id of the tag to delete.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function deleteTag(tagGroupId, tagId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/tag-groups/' + tagGroupId + '/tags/' + tagId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Tag a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {int} tagId - The id of the tag.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function tagConversation(conversationId, tagId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId + '/tags/' + tagId, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Untag a conversation.
 *
 * @param {int} conversationId - The id of the conversation.
 * @param {int} tagId - The id of the tag.
 * @param {object} token - The token to use, for getting conversation.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversation.
 */
export function untagConversation(conversationId, tagId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/conversations/' + conversationId + '/tags/' + tagId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Save a field for a tag group.
 *
 * @param {int} tagGroupId - The id of the tag group.
 * @param {string} field - The key for the field.
 * @param {string} value - The value to save.
 * @param {object} token - The token to use, for saving tag group.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving tag group.
 */
export function saveTagGroup(tagGroupId, field, value, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  var modifier = {};
  modifier[field] = value;
  fetch(getApiEndpoint() + '/tag-groups/' + tagGroupId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: modifier,
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Save a field for a tag.
 *
 * @param {int} tagGroupId - The id of the tag group.
 * @param {int} tagId - The id of the tag.
 * @param {string} field - The key for the field.
 * @param {string} value - The value to save.
 * @param {object} token - The token to use, for saving tag.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving tag.
 */
export function saveTag(tagGroupId, tagId, field, value, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  var modifier = {};
  modifier[field] = value;
  fetch(getApiEndpoint() + '/tag-groups/' + tagGroupId + '/tags/' + tagId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: modifier,
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}



// WEBPACK FOOTER //
// ./src/utility/tag.js